"use client";

import { Box, Container, Text, VStack } from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useSWR from "swr";
import { PricingPlan, type BillingPeriod } from "./data/pricingData";
import BillingToggle from "./components/BillingToggle";
import PricingTable from "./components/PricingTable";
import PricingCards from "./components/PricingCards";
import VietnamContactBanner from "./components/VietnamContactBanner";
import { useIsVietnam } from "./_hooks/useIsVietnam";
import { paddleService } from "@/_services/membershipServices/payment";
import { amountByPriceIdFromCatalog } from "./_lib/catalogAmounts";
import { useAuthStore } from "@/_stores/useAuthStore";
import { useRouter, useSearchParams } from "next/navigation";
import { usePricingStorage } from "@/_hooks/usePricingStorage";
import { useTranslations } from "next-intl";
import { usePaymentCheckout } from "@/_hooks/payments/usePaymentCheckout";
import TrustSlideshow from "./components/TrustSlideshow";
import EnterpriseCTA from "./components/EnterpriseCTA";
import PricingFAQ from "./components/PricingFAQ";
import { PricingHeader } from "./components/PricingHeader";
import { PRICING_IDS } from "./_constants/pricing.ids";
import { Footer } from "@/app/[locale]/landing/_components/Footer";
import {
  MembershipUsageResponse,
  usageService,
} from "@/_services/membershipServices/usage";
import {
  BIEvents,
  trackEvent,
  useTimingEvent,
} from "@/_components/lib/mixpanel/mixpanelClient";

function PricingPageContent() {
  const t = useTranslations("pricing.page");
  const pricingStorage = usePricingStorage();

  const searchParams = useSearchParams();
  const pricingPlanId = searchParams.get("planId");
  const billingParam = searchParams.get("billing");

  const [billing, setBilling] = useState<BillingPeriod>(() =>
    billingParam === "annual" || billingParam === "monthly"
      ? billingParam
      : "monthly",
  );
  const lastAutoOpenedPlanRef = useRef<string | null>(null);
  const enterpriseRef = useRef<HTMLDivElement>(null);

  const { token, user } = useAuthStore();
  const router = useRouter();
  const { isVietnam } = useIsVietnam();
  const { startTrack, stopTrack } = useTimingEvent(
    BIEvents.PRICING_STAY_ON_PAGE,
    { page: "pricing" },
  );

  useEffect(() => {
    startTrack();
    return () => {
      stopTrack();
    };
  }, []);

  const { data: catalog, isLoading: isCatalogLoading } = useSWR(
    "/payment/catalog",
    () => paddleService.getProductPrice(),
    { revalidateOnFocus: false },
  );

  const amountByPriceId = useMemo(
    () => amountByPriceIdFromCatalog(catalog?.items),
    [catalog],
  );
  const { data: usageData } = useSWR<MembershipUsageResponse>(
    token ? "/membership/usage" : null,
    () => usageService.getUsage(),
    {
      revalidateOnFocus: false,
    },
  );

  const { Drawer, SuccessDialog, isPaddleReady, openCheckoutPanel } =
    usePaymentCheckout({
      showSuccessDialogOnPurchase: true,
    });

  const handleBillingChange = useCallback(
    (period: BillingPeriod) => {
      trackEvent(BIEvents.PRICING_BILLING_TOGGLE, {
        billing: period,
        previous_billing: billing,
      });
      setBilling(period);
      const params = new URLSearchParams(searchParams.toString());
      params.set("billing", period);
      router.replace(`?${params.toString()}`, { scroll: false });
    },
    [billing, router, searchParams],
  );

  useEffect(() => {
    if (
      !isPaddleReady ||
      !pricingPlanId ||
      lastAutoOpenedPlanRef.current === pricingPlanId ||
      !user?.userInfo.email
    ) {
      return;
    }

    lastAutoOpenedPlanRef.current = pricingPlanId;
    openCheckoutPanel(pricingPlanId);
  }, [pricingPlanId, openCheckoutPanel, isPaddleReady, user?.userInfo.email]);

  const scrollToEnterprise = useCallback(() => {
    enterpriseRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, []);

  const handleSelectPlan = (plan: PricingPlan) => {
    trackEvent(BIEvents.PRICING_SELECT_PLAN, {
      plan_id: plan.id,
      billing,
      authenticated: Boolean(token),
    });

    switch (plan.id) {
      case "free":
        router.push("/auth/login");
        break;
      case "pro":
      case "ultimate":
        if (!token) {
          pricingStorage.setPricingPlanSelected(
            plan.pricingId[billing],
            billing,
          );
          router.push("/auth/login");
          return;
        }

        openCheckoutPanel(plan.pricingId[billing]);
        break;
      case "enterprise":
        scrollToEnterprise();
        break;
      default:
        break;
    }
  };

  return (
    <>
      <Box minH="100dvh" bg="neutral.50" display="flex" flexDirection="column">
        <PricingHeader />

        <Box as="main" flex={1}>
          <Box
            bg="linear-gradient(180deg, rgba(99,91,255,0.06) 0%, rgba(250,248,245,0) 72%)"
            pt={{ base: "10rem", md: "8rem" }}
            pb={{ base: 8, md: 10 }}
          >
            <Container maxW="9xl" px={{ base: 5, md: 6 }}>
              <VStack
                id={PRICING_IDS.sections.plans}
                gap={{ base: 6, md: 8 }}
                textAlign="center"
                scrollMarginTop="5rem"
              >
                <VStack gap={3} maxW="3xl">
                  <Text
                    textStyle="xs"
                    fontWeight="bold"
                    letterSpacing="0.12em"
                    textTransform="uppercase"
                    color="primary.600"
                  >
                    {t("eyebrow")}
                  </Text>
                  <Text
                    as="h1"
                    fontFamily="heading"
                    textStyle={{ base: "2xl", md: "4xl", lg: "5xl" }}
                    fontWeight="bold"
                    color="neutral.900"
                    letterSpacing="-0.03em"
                    lineHeight={1.1}
                  >
                    {t("title")}
                  </Text>
                  <Text
                    textStyle={{ base: "md", md: "lg" }}
                    color="neutral.600"
                    lineHeight="tall"
                  >
                    {t("subtitle")}
                  </Text>
                </VStack>
                <BillingToggle
                  billing={billing}
                  setBilling={handleBillingChange}
                />
              </VStack>
            </Container>
          </Box>

          <Container
            maxW="9xl"
            px={{ base: 5, md: 6 }}
            pb={{ base: 12, lg: 16 }}
          >
            <VStack gap={{ base: 10, md: 14 }} align="stretch">
              {isVietnam ? <VietnamContactBanner /> : null}

              <PricingCards
                billing={billing}
                onSelect={handleSelectPlan}
                amountByPriceId={amountByPriceId}
                isCatalogLoading={isCatalogLoading}
                currentPriceId={usageData?.product_rate.priceId}
              />

              <TrustSlideshow />

              <VStack
                id={PRICING_IDS.sections.compare}
                gap={3}
                textAlign="center"
                scrollMarginTop="5rem"
              >
                <Text
                  as="h2"
                  fontFamily="heading"
                  textStyle={{ base: "2xl", md: "3xl", lg: "4xl" }}
                  fontWeight="bold"
                  color="neutral.900"
                  letterSpacing="-0.02em"
                >
                  {t("compareHeading")}
                </Text>
                <Text textStyle="md" color="neutral.600" maxW="xl">
                  {t("compareSubtitle")}
                </Text>
              </VStack>

              <Box
                overflowX={{ base: "auto", lg: "visible" }}
                mx={{ base: -5, lg: 0 }}
                px={{ base: 5, lg: 0 }}
                pb={{ base: 2, lg: 0 }}
                css={{
                  WebkitOverflowScrolling: "touch",
                  "& table": { minWidth: "720px" },
                }}
              >
                <PricingTable
                  billing={billing}
                  onSelect={handleSelectPlan}
                  amountByPriceId={amountByPriceId}
                  isCatalogLoading={isCatalogLoading}
                />
              </Box>

              <Box
                ref={enterpriseRef}
                id={PRICING_IDS.sections.enterprise}
                scrollMarginTop="5rem"
              >
                <EnterpriseCTA />
              </Box>

              <Box id={PRICING_IDS.sections.faq} scrollMarginTop="5rem">
                <PricingFAQ />
              </Box>

              <Text
                textAlign="center"
                textStyle="sm"
                color="neutral.500"
                maxW="2xl"
                mx="auto"
                lineHeight="tall"
              >
                {t("footerNote")}
              </Text>
            </VStack>
          </Container>
          <Footer />
        </Box>
      </Box>
      {Drawer}
      {SuccessDialog}
    </>
  );
}

export function PricingPageClient() {
  return <PricingPageContent />;
}
